/**
 * « Financial Funnel » — du CA HT à la MC4, une marche par niveau de marge.
 *
 * Chaque marche a la largeur de sa part du CA HT (jamais moins d'un tiers,
 * pour rester lisible), et le trait qui la relie à la suivante dessine la
 * perte. Légende à gauche (part + nom), formules à droite : le graphique se
 * lit seul. SVG maison : aucune bibliothèque de graphiques ne fait un entonnoir
 * qui respecte nos couleurs et nos marqueurs de « manquant ».
 *
 * Une marche `null` (coût d'envoi non réglé, charges pas chargées…) garde la
 * largeur de la précédente et affiche « — » : la forme reste, le trou se voit.
 */

import type { FunnelStep } from '../lib/summary';
import { formatMoney, formatPercent } from '../lib/format';
import { CATEGORICAL_COLORS, COLORS } from '../lib/theme';
import { Card } from './Card';

const STEP_COLORS: Record<FunnelStep['key'], string> = {
  revenue: CATEGORICAL_COLORS[0],
  mc1: CATEGORICAL_COLORS[4],
  mc1Prime: CATEGORICAL_COLORS[1],
  mc2: CATEGORICAL_COLORS[8],
  mc3: CATEGORICAL_COLORS[6],
  mc4: CATEGORICAL_COLORS[2],
};

const FORMULAS: ReadonlyArray<[string, string]> = [
  ['MC1', 'CA HT − Cogs − frais de paiement − retenues abos'],
  ["MC1'", "MC1 − frais d'envoi"],
  ['MC2', 'MC1 − Ads'],
  ['MC3', 'MC2 − Branding'],
  ['MC4', 'MC3 − Charges'],
];

const WIDTH = 1000;
const STEP_HEIGHT = 84;
const MIN_SHARE = 0.34;

export function FinancialFunnel({ steps, currency }: { steps: FunnelStep[] | null; currency: string }) {
  const money = (value: number) => formatMoney(value, currency, true);
  const height = (steps?.length ?? 6) * STEP_HEIGHT;

  // Largeur de chaque marche : sa part du CA, bornée ; une marche inconnue reprend la précédente.
  const widths: number[] = [];
  for (const step of steps ?? []) {
    const previous = widths[widths.length - 1] ?? WIDTH;
    const share = step.percent !== null ? Math.max(MIN_SHARE, Math.min(1, step.percent / 100)) : previous / WIDTH;
    widths.push(step.key === 'revenue' ? WIDTH : Math.min(previous, share * WIDTH));
  }

  return (
    <Card title="Financial Funnel" subtitle="Du CA HT (kits + pianos + abonnements) à la MC4, part de chaque niveau dans le CA HT — période affichée" className="chart-grid__wide">
      {!steps ? (
        <p className="empty">En attente de la MC2 avec abo (coûts de revient, frais Shopify Payments, pub, abonnements).</p>
      ) : (
        <div className="funnel">
          <ul className="funnel__legend">
            {steps.map((step) => (
              <li key={step.key} className="legend__item">
                <span className="legend__key" style={{ background: STEP_COLORS[step.key] }} aria-hidden="true" />
                <strong>{step.percent !== null ? formatPercent(step.percent) : '—'}</strong>&nbsp;{step.label}
              </li>
            ))}
          </ul>
          <svg className="funnel__chart" viewBox={`0 0 ${WIDTH} ${height}`} role="img" aria-label="Entonnoir financier, du CA HT à la MC4">
            {steps.map((step, index) => {
              const top = widths[index] ?? WIDTH;
              const bottom = index + 1 < steps.length ? (widths[index + 1] ?? top) : top * 0.7;
              const y = index * STEP_HEIGHT;
              const points = [
                [(WIDTH - top) / 2, y],
                [(WIDTH + top) / 2, y],
                [(WIDTH + bottom) / 2, y + STEP_HEIGHT],
                [(WIDTH - bottom) / 2, y + STEP_HEIGHT],
              ]
                .map(([x, yy]) => `${x},${yy}`)
                .join(' ');
              return (
                <g key={step.key}>
                  <polygon points={points} fill={STEP_COLORS[step.key]} />
                  <text x={WIDTH / 2} y={y + STEP_HEIGHT / 2 - 2} textAnchor="middle" fill={COLORS.surface} fontSize="24">
                    <tspan fontWeight="700">{step.percent !== null ? formatPercent(step.percent) : '—'}</tspan>
                    {step.value !== null ? ` (${money(step.value)})` : ''}
                  </text>
                  <text x={WIDTH / 2} y={y + STEP_HEIGHT / 2 + 22} textAnchor="middle" fill={COLORS.surface} fontSize="17" fontWeight="600">
                    {step.label}
                  </text>
                </g>
              );
            })}
          </svg>
          <ul className="funnel__formulas">
            {FORMULAS.map(([name, formula]) => (
              <li key={name}>
                <em>{name}</em> : {formula}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
